import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Button,
  Upload,
  message,
  Typography,
  Table,
  Select,
  Alert,
  Space,
  Spin,
  Tabs,
  Badge,
  Radio,
  Statistic,
  Row,
  Col,
  Card
} from 'antd';
import {
  UploadOutlined,
  FileSearchOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { Customer } from '../types.ts';
import apiClient from '../utils/axiosConfig.ts';
import { customerAPI } from '../services/api.ts';

const { Text } = Typography;
const { Option } = Select;

interface InventoryImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface RowData {
  row: number;
  codice: string;
  descrizione: string;
  taglia: string;
  colore: string;
  costo: number;
  vendita: number;
  cod_dog: string;
  weight_gross: number;
  client_code: string | null;
}

interface AnalysisItem {
  category: string;
  reason: string;
  product_id?: number;
  client_code_excel?: string;
  client_code_db?: string;
  internal_code?: string;
  description?: string;
  size_db?: string;
  taglia_excel?: string;
  row_data: RowData;
}

interface SkippedItem {
  row: number;
  reason: string;
  codice: string;
}

interface AnalysisResult {
  status: string;
  total_rows: number;
  customer_name: string;
  matched: AnalysisItem[];
  to_update: AnalysisItem[];
  conflicts: AnalysisItem[];
  new_products: AnalysisItem[];
  skipped: SkippedItem[];
  summary: {
    matched_count: number;
    to_update_count: number;
    conflict_count: number;
    new_product_count: number;
    skipped_count: number;
  };
}

interface ImportResult {
  success: boolean;
  updated_count: number;
  conflict_resolved_count: number;
  associations_count: number;
  error_count: number;
  errors: any[];
}

const InventoryImportModal: React.FC<InventoryImportModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | undefined>(undefined);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [conflictResolutions, setConflictResolutions] = useState<Record<number, boolean>>({});
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    if (visible) {
      loadCustomers();
    }
  }, [visible]);

  const handleCancel = () => {
    setCurrentStep(0);
    setFileList([]);
    setSelectedCustomer(undefined);
    setAnalysisResult(null);
    setConflictResolutions({});
    setImportResult(null);
    onCancel();
  };

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getCustomers();
      setCustomers(response);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.xls,.xlsx',
    beforeUpload: () => false,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList.slice(-1));
    },
    fileList,
  };

  // Step 0: Upload e analisi
  const handleUploadAndAnalyze = async () => {
    if (fileList.length === 0) {
      message.error('Seleziona un file da importare');
      return;
    }
    if (!selectedCustomer) {
      message.error('Seleziona un cliente');
      return;
    }

    const file = fileList[0].originFileObj;
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('customer_id', selectedCustomer.toString());

      const response = await apiClient.post('/inventory-import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data: AnalysisResult = response.data;
      setAnalysisResult(data);

      // Inizializza risoluzioni conflitti (default: usa Excel)
      const defaultResolutions: Record<number, boolean> = {};
      data.conflicts.forEach((c: AnalysisItem) => {
        if (c.product_id !== undefined) {
          defaultResolutions[c.product_id] = true;
        }
      });
      setConflictResolutions(defaultResolutions);

      message.success(`Analisi completata: ${data.total_rows} righe elaborate`);
      setCurrentStep(1);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore durante l\'analisi del file';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Esecuzione import
  const handleExecuteImport = async () => {
    if (!analysisResult) return;

    setLoading(true);
    try {
      const conflictResolutionList = analysisResult.conflicts.map((c: AnalysisItem) => ({
        product_id: c.product_id,
        client_code_excel: c.client_code_excel,
        use_excel: c.product_id !== undefined ? (conflictResolutions[c.product_id] ?? true) : true
      }));

      // I prodotti assenti in anagrafica NON vengono creati: sono solo segnalati
      const response = await apiClient.post('/inventory-import/execute', {
        updates: analysisResult.to_update,
        conflict_resolutions: conflictResolutionList,
        customer_id: selectedCustomer
      });

      const result: ImportResult = response.data;
      setImportResult(result);

      if (result.success) {
        message.success('Importazione completata con successo');
        onSuccess();
      } else {
        message.warning(`Importazione completata con ${result.error_count} errori`);
      }

      setCurrentStep(3);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore durante l\'importazione';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { title: 'Seleziona File', icon: <UploadOutlined /> },
    { title: 'Analisi', icon: <FileSearchOutlined /> },
    { title: 'Conflitti', icon: <WarningOutlined /> },
    { title: 'Risultati', icon: <CheckCircleOutlined /> },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ padding: '20px 0' }}>
            <Upload.Dragger {...uploadProps} style={{ marginBottom: 24 }}>
              <p className="ant-upload-drag-icon"><UploadOutlined /></p>
              <p className="ant-upload-text">Clicca o trascina il file Excel in quest'area</p>
              <p className="ant-upload-hint">Supportati: file .xls, .xlsx</p>
            </Upload.Dragger>

            <div style={{ marginBottom: 24 }}>
              <Text strong>Seleziona Cliente *</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Seleziona il cliente"
                value={selectedCustomer}
                onChange={setSelectedCustomer}
                showSearch
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {customers.map(customer => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.company}
                  </Option>
                ))}
              </Select>
              <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                Il cliente determina la logica di estrazione del codice prodotto dalla descrizione
              </div>
            </div>

            <Alert
              message="Colonne Excel richieste"
              description="CODICE, DESCRIZIONE, TAGLIA-TEBRO, COLORE-TEBRO, COSTO, VENDITA, COD_DOG, PESO_LORDO_1_PZ"
              type="info"
              style={{ marginBottom: 24 }}
            />

            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                onClick={handleUploadAndAnalyze}
                loading={loading}
                disabled={fileList.length === 0 || !selectedCustomer}
              >
                Analizza File
              </Button>
            </div>
          </div>
        );

      case 1: {
        if (!analysisResult) return null;
        const sm = analysisResult.summary;

        const analysisColumns = [
          { title: 'Riga', width: 60, render: (_: any, record: any) => record.row_data?.row || record.row },
          { title: 'Codice', width: 130, render: (_: any, record: any) => record.row_data?.codice || record.codice },
          { title: 'Descrizione', ellipsis: true, render: (_: any, record: any) => record.row_data?.descrizione || record.descrizione },
          { title: 'Cod. Cliente', width: 130, render: (_: any, record: any) => record.row_data?.client_code || '-' },
          { title: 'Motivo', dataIndex: 'reason', width: 250 },
        ];

        const skippedColumns = [
          { title: 'Riga', dataIndex: 'row', width: 60 },
          { title: 'Codice', dataIndex: 'codice', width: 130 },
          { title: 'Motivo', dataIndex: 'reason' },
        ];

        const tabItems = [
          {
            key: 'new',
            label: <Badge count={sm.new_product_count} showZero style={{ backgroundColor: sm.new_product_count > 0 ? '#ff4d4f' : undefined }}><span style={{ marginRight: 8 }}>Non in Anagrafica</span></Badge>,
            children: (
              <Table
                dataSource={analysisResult.new_products.map((p, i) => ({ key: i, ...p }))}
                columns={analysisColumns}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            )
          },
          {
            key: 'update',
            label: <Badge count={sm.to_update_count} showZero><span style={{ marginRight: 8 }}>Da Aggiornare</span></Badge>,
            children: (
              <Table
                dataSource={analysisResult.to_update.map((p, i) => ({ key: i, ...p }))}
                columns={analysisColumns}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            )
          },
          {
            key: 'matched',
            label: <Badge count={sm.matched_count} showZero><span style={{ marginRight: 8 }}>Già Presenti</span></Badge>,
            children: (
              <Table
                dataSource={analysisResult.matched.map((p, i) => ({ key: i, ...p }))}
                columns={analysisColumns}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            )
          },
          {
            key: 'conflicts',
            label: <Badge count={sm.conflict_count} showZero overflowCount={999} style={{ backgroundColor: sm.conflict_count > 0 ? '#faad14' : undefined }}><span style={{ marginRight: 8 }}>Conflitti</span></Badge>,
            children: (
              <Table
                dataSource={analysisResult.conflicts.map((p, i) => ({ key: i, ...p }))}
                columns={[
                  ...analysisColumns,
                  { title: 'Cod. DB', dataIndex: 'client_code_db', width: 130 },
                ]}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            )
          },
          {
            key: 'skipped',
            label: <Badge count={sm.skipped_count} showZero><span style={{ marginRight: 8 }}>Saltati</span></Badge>,
            children: (
              <Table
                dataSource={analysisResult.skipped.map((p: SkippedItem, i: number) => ({ key: i, ...p }))}
                columns={skippedColumns}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            )
          },
        ];

        return (
          <div>
            {sm.new_product_count > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${sm.new_product_count} prodotti del file non sono presenti in anagrafica`}
                description="Non vengono creati da questa importazione: sono elencati nella scheda 'Non in Anagrafica'. Inserirli a mano da Anagrafica → Prodotti e ripetere l'importazione."
              />
            )}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={4}><Card size="small"><Statistic title="Non in Anagrafica" value={sm.new_product_count} valueStyle={{ color: sm.new_product_count > 0 ? '#ff4d4f' : undefined }} /></Card></Col>
              <Col span={5}><Card size="small"><Statistic title="Da Aggiornare" value={sm.to_update_count} valueStyle={{ color: '#1890ff' }} /></Card></Col>
              <Col span={5}><Card size="small"><Statistic title="Già Presenti" value={sm.matched_count} /></Card></Col>
              <Col span={5}><Card size="small"><Statistic title="Conflitti" value={sm.conflict_count} valueStyle={{ color: sm.conflict_count > 0 ? '#faad14' : undefined }} /></Card></Col>
              <Col span={5}><Card size="small"><Statistic title="Saltati" value={sm.skipped_count} valueStyle={{ color: '#999' }} /></Card></Col>
            </Row>

            <Tabs defaultActiveKey="new" items={tabItems} />

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Space>
                <Button onClick={() => setCurrentStep(0)}>Indietro</Button>
                {sm.conflict_count > 0 ? (
                  <Button type="primary" onClick={() => setCurrentStep(2)}>
                    Risolvi Conflitti ({sm.conflict_count})
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={handleExecuteImport}
                    loading={loading}
                    disabled={sm.to_update_count === 0}
                  >
                    Esegui Importazione
                  </Button>
                )}
              </Space>
            </div>
          </div>
        );
      }

      case 2: {
        if (!analysisResult) return null;

        const conflictColumns = [
          { title: 'Riga', width: 60, render: (_: any, record: AnalysisItem) => record.row_data?.row },
          { title: 'Codice Interno', width: 130, render: (_: any, record: AnalysisItem) => record.internal_code || record.row_data?.codice },
          { title: 'Descrizione', ellipsis: true, render: (_: any, record: AnalysisItem) => record.description || record.row_data?.descrizione },
          { title: 'Taglia DB', width: 80, dataIndex: 'size_db' },
          { title: 'Taglia Excel', width: 90, render: (_: any, record: AnalysisItem) => record.taglia_excel || record.row_data?.taglia },
          { title: 'Cod. Cliente DB', width: 130, dataIndex: 'client_code_db', render: (val: string) => <Text type="secondary">{val}</Text> },
          { title: 'Cod. Cliente Excel', width: 140, dataIndex: 'client_code_excel', render: (val: string) => <Text type="success">{val}</Text> },
          {
            title: 'Scelta',
            width: 180,
            render: (_: any, record: AnalysisItem) => (
              <Radio.Group
                value={record.product_id !== undefined ? (conflictResolutions[record.product_id] ?? true) : true}
                onChange={(e) => {
                  if (record.product_id !== undefined) {
                    setConflictResolutions(prev => ({ ...prev, [record.product_id!]: e.target.value }));
                  }
                }}
                size="small"
              >
                <Radio value={true}>Excel</Radio>
                <Radio value={false}>DB</Radio>
              </Radio.Group>
            )
          },
        ];

        return (
          <div>
            <Alert
              message={`${analysisResult.conflicts.length} conflitti da risolvere`}
              description="Per ogni conflitto, scegli se usare il codice cliente dal file Excel o mantenere quello nel database."
              type="warning"
              style={{ marginBottom: 16 }}
            />

            <Table
              dataSource={analysisResult.conflicts.map((c, i) => ({ key: i, ...c }))}
              columns={conflictColumns}
              pagination={{ pageSize: 10 }}
              size="small"
              scroll={{ x: 900 }}
              style={{ marginBottom: 24 }}
            />

            <div style={{ textAlign: 'center' }}>
              <Space>
                <Button onClick={() => setCurrentStep(1)}>Indietro</Button>
                <Button
                  type="primary"
                  onClick={handleExecuteImport}
                  loading={loading}
                >
                  Conferma e Importa
                </Button>
              </Space>
            </div>
          </div>
        );
      }

      case 3: {
        if (!importResult) return null;

        return (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: 16 }}>
              {importResult.success ? (
                <span style={{ color: '#52c41a' }}>Importazione Completata</span>
              ) : (
                <span style={{ color: '#faad14' }}>Importazione completata con {importResult.error_count} errori</span>
              )}
            </div>

            <Row gutter={16} style={{ marginBottom: 24 }} justify="center">
              <Col span={5}><Card size="small"><Statistic title="Aggiornati" value={importResult.updated_count} valueStyle={{ color: '#1890ff' }} /></Card></Col>
              <Col span={5}><Card size="small"><Statistic title="Conflitti Risolti" value={importResult.conflict_resolved_count} /></Card></Col>
              <Col span={5}><Card size="small"><Statistic title="Associazioni" value={importResult.associations_count} valueStyle={{ color: '#722ed1' }} /></Card></Col>
              <Col span={4}><Card size="small"><Statistic title="Errori" value={importResult.error_count} valueStyle={{ color: importResult.error_count > 0 ? '#ff4d4f' : undefined }} /></Card></Col>
            </Row>

            {importResult.errors.length > 0 && (
              <Table
                dataSource={importResult.errors.map((e: any, i: number) => ({ key: i, ...e }))}
                columns={[
                  { title: 'Riga', dataIndex: 'row', width: 60 },
                  { title: 'Codice', dataIndex: 'codice', width: 130 },
                  { title: 'Errore', dataIndex: 'error' },
                ]}
                pagination={{ pageSize: 10 }}
                size="small"
                style={{ marginBottom: 24 }}
              />
            )}

            <Button type="primary" onClick={handleCancel}>Chiudi</Button>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Modal
      title="Importa da Giacenze"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Steps
          current={currentStep}
          items={steps}
          style={{ marginBottom: 32 }}
        />
        {renderStepContent()}
      </Spin>
    </Modal>
  );
};

export default InventoryImportModal;
